import { useCallback } from "react";

import {
  MISSING_FIELD_IDS_KEY,
  MISSING_FIELD_NAMES_KEY,
  REQUIRED_FOR_STAGE_KEY,
  REVIEW_TRIGGER_KEY,
  TARGET_STAGE_NAME_KEY
} from "constants/deal";
import { useModals } from "../shared-hooks/use-modals";
import { useNavigate } from "../shared-hooks/use-navigate";

export interface CannotMoveToStageMissingField {
  id: number;
  name: string;
}

export interface ShowCannotMoveToStageParams {
  dealId: number;
  targetStageName: string;
  targetStageId: number;
  /** When already on deal page (e.g. drawer open), only names are used for the message; no navigation. */
  missingFields: CannotMoveToStageMissingField[] | string[];
  /** If true, "Review Missing Fields" just closes the modal (user already on deal with drawer). */
  alreadyOnDeal?: boolean;
  /** Optional extra callback when user cancels (e.g. revert pipeline move). */
  onCancel?: () => void;
}

/**
 * Shared hook to show the white "Cannot move to stage" dialog and optionally navigate to the deal
 * with required-fields params so the Deal page opens the characteristics panel/drawer.
 */
export function useCannotMoveToStageHook() {
  const { confirmModal, closeConfirmModal } = useModals();
  const navigate = useNavigate();

  const showCannotMoveToStageModal = useCallback(
    (params: ShowCannotMoveToStageParams) => {
      const {
        dealId,
        targetStageName,
        targetStageId,
        missingFields,
        alreadyOnDeal = false,
        onCancel
      } = params;

      const fieldsList =
        missingFields.length > 0
          ? missingFields.map((f) => (typeof f === "string" ? f : f.name)).join(", ")
          : "";

      confirmModal({
        title: "Cannot move to stage",
        msg: `To move to ${targetStageName} you must fill in the following required fields: ${fieldsList}.\n\nReview and save them, then you can move the deal to the new stage.`,
        actionLabel: "Review Missing Fields",
        action: () => {
          closeConfirmModal();
          if (!alreadyOnDeal) {
            const missingFieldIds =
              missingFields.length > 0 &&
              typeof missingFields[0] === "object" &&
              "id" in missingFields[0]
                ? (missingFields as CannotMoveToStageMissingField[]).map((f) => f.id).join(",")
                : "";
            navigate({
              route: "deal",
              params: { deal_id: String(dealId) },
              search: {
                [REQUIRED_FOR_STAGE_KEY]: String(targetStageId),
                [MISSING_FIELD_IDS_KEY]: missingFieldIds,
                [TARGET_STAGE_NAME_KEY]: targetStageName,
                [MISSING_FIELD_NAMES_KEY]: fieldsList,
                [REVIEW_TRIGGER_KEY]: String(Date.now())
              }
            });
          }
        },
        onCancel: () => {
          closeConfirmModal();
          onCancel?.();
        }
      });
    },
    [confirmModal, closeConfirmModal, navigate]
  );

  return { showCannotMoveToStageModal };
}
